import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, X, AlertCircle } from "lucide-react";
import "./PurchaseOrder.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const PURCHASE_API = `${API_BASE}/purchases`;
const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function PurchaseOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [acceptItems, setAcceptItems] = useState([]);
  const [acceptRemark, setAcceptRemark] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [expenseName, setExpenseName] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${PURCHASE_API}/orders`);
      const data = (res.data || [])
        .map((o) => ({
          ...o,
          line_type: String(o.line_type || "ITEM").toUpperCase(),
          quantity: Number(o.quantity || 0),
          rate: Number(o.rate || 0),
          tax_amount: Number(o.tax_amount || 0),
        }))
        .map((o) => {
          const computedAmount =
            o.line_type === "EXPENSE"
              ? Number(o.amount || 0)
              : Number(o.amount || 0) > 0
                ? Number(o.amount || 0)
                : Number(o.quantity || 0) * Number(o.rate || 0);

          return {
            ...o,
            amount: Number.isFinite(computedAmount) ? computedAmount : 0,
            quantity: Number.isFinite(o.quantity) ? o.quantity : 0,
          };
        });
      setOrders(data);
    } catch (err) {
      console.error("Error fetching purchase orders:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const groupedOrders = useMemo(() => {
    const map = new Map();

    for (const row of orders) {
      const groupKey = row.order_group_id || `ROW-${row.id}`;
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          groupKey,
          order_group_id: row.order_group_id || null,
          order_id: row.order_id,
          supplier_id: row.supplier_id,
          supplier_name: row.supplier_name,
          order_date: row.order_date,
          created_at: row.created_at,
          status: row.status,
          reject_reason: row.reject_reason,
          action_id: row.id,
          lines: [],
        });
      }

      const group = map.get(groupKey);
      group.lines.push(row);

      if ((row.status || "").toUpperCase() === "PENDING") {
        group.status = "PENDING";
        group.action_id = row.id;
      }
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at)
    );
  }, [orders]);

  const tableRows = useMemo(() => {
    const rows = [];

    for (const group of groupedOrders) {
      const itemLines = group.lines.filter((line) => line.line_type !== "EXPENSE");
      const expenseLines = group.lines.filter((line) => line.line_type === "EXPENSE");
      const pairCount = Math.max(itemLines.length, expenseLines.length, 1);

      for (let i = 0; i < pairCount; i += 1) {
        const item = itemLines[i] || null;
        const expense = expenseLines[i] || null;
        const itemAmount = Number(item?.amount || 0);
        const expenseAmount = Number(expense?.amount || 0);

        rows.push({
          key: `${group.groupKey}-${i}`,
          order_id: group.order_group_id || group.order_id || `PUR-${String(group.action_id).padStart(6, "0")}`,
          supplier_id: group.supplier_id,
          supplier_name: group.supplier_name,
          order_date: group.order_date,
          created_at: group.created_at,
          status: group.status,
          reject_reason: group.reject_reason,
          action_id: group.action_id,
          product_name: item?.product_name || "-",
          quantity: item ? Number(item.quantity || 0) : "-",
          tax_amount: item ? Number(item.tax_amount || 0) : null,
          product_amount: item ? itemAmount : null,
          expense_name: expense?.expense_name || "-",
          expense_amount: expense ? expenseAmount : null,
          total: itemAmount + expenseAmount,
        });
      }
    }

    return rows;
  }, [groupedOrders]);

  useEffect(() => {
    if (tableRows.length) {
      console.log("purchase order rows statuses", tableRows.map((row) => row.status));
    }
  }, [tableRows]);

  const pendingCount = useMemo(
    () => groupedOrders.filter((o) => (o.status || "").toUpperCase() === "PENDING").length,
    [groupedOrders]
  );

  const acceptedCount = useMemo(
    () => groupedOrders.filter((o) => (o.status || "").toUpperCase() === "ACCEPTED").length,
    [groupedOrders]
  );

  const rejectedCount = useMemo(
    () => groupedOrders.filter((o) => (o.status || "").toUpperCase() === "REJECTED").length,
    [groupedOrders]
  );

  const totalQty = useMemo(
    () => orders.reduce((sum, o) => sum + (o.line_type === "EXPENSE" ? 0 : Number(o.quantity || 0)), 0),
    [orders]
  );

  const handleAcceptOrder = async (id) => {
    const group = groupedOrders.find((order) =>
      order.lines.some((line) => Number(line.id) === Number(id))
    );
    const itemLines = (group?.lines || []).filter((line) => line.line_type !== "EXPENSE");

    setSelectedOrderId(id);
    setAcceptItems(
      itemLines.map((line) => ({
        id: line.id,
        product_name: line.product_name || line.inventory_name || "",
        category: line.category || line.inventory_category || "",
        quantity: String(Number(line.quantity || 0) || ""),
        rate: String(Number(line.rate || 0) || ""),
        tax_amount: String(Number(line.tax_amount || 0)),
      }))
    );
    setAcceptRemark("");
    setShowAcceptModal(true);
  };

  const updateAcceptItem = (index, key, value) => {
    setAcceptItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const handleAcceptSubmit = async () => {
    if (!acceptRemark.trim()) {
      alert("Remark is required to accept the order");
      return;
    }

    const invalidItem = acceptItems.find(
      (item) =>
        !String(item.product_name || "").trim() ||
        !String(item.category || "").trim() ||
        Number(item.quantity || 0) <= 0 ||
        Number(item.rate || 0) <= 0
    );

    if (invalidItem) {
      alert("Product name, category, quantity and rate are required before accepting.");
      return;
    }

    try {
      await axios.put(`${PURCHASE_API}/orders/${selectedOrderId}/accept`, {
        remark: acceptRemark,
        items: acceptItems.map((item) => ({
          id: item.id,
          product_name: String(item.product_name || "").trim(),
          category: String(item.category || "").trim(),
          quantity: Number(item.quantity || 0),
          rate: Number(item.rate || 0),
          tax_amount: Number(item.tax_amount || 0),
        })),
      });
      setShowAcceptModal(false);
      setAcceptItems([]);
      setAcceptRemark("");
      await fetchOrders();
    } catch (err) {
      console.error("Accept order error:", err);
      alert(err?.response?.data?.message || "Failed to accept order");
    }
  };

  const handleRejectOrder = async (id) => {
    setSelectedOrderId(id);
    setRejectReason("");
    setExpenseName("");
    setExpenseAmount("");
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert("Reason is required for rejection");
      return;
    }

    if (expenseAmount && Number.isNaN(parseFloat(expenseAmount))) {
      alert("Expense amount must be a valid number");
      return;
    }

    try {
      await axios.put(`${PURCHASE_API}/orders/${selectedOrderId}/reject`, {
        reject_reason: rejectReason,
        expense_name: expenseName,
        expense_amount: expenseAmount ? Number(expenseAmount) : null,
      });
      setShowRejectModal(false);
      setRejectReason("");
      setExpenseName("");
      setExpenseAmount("");
      alert("Purchase order rejected successfully");
      await fetchOrders();
    } catch (err) {
      console.error("Reject order error:", err);
      alert(err?.response?.data?.message || "Failed to reject order");
    }
  };

  return (
    <div className="page-container">
      <div className="po-header">
        <button type="button" className="po-back-btn" onClick={() => navigate("/purchases")}>
          ← Back
        </button>
        <h2>Purchase Orders</h2>
        <button type="button" className="po-refresh-btn" onClick={fetchOrders}>
          Refresh
        </button>
      </div>

      <div className="po-stats">
        <div className="po-stat-card">
          <div className="po-stat-label">Pending</div>
          <div className="po-stat-value pending">{pendingCount}</div>
        </div>
        <div className="po-stat-card">
          <div className="po-stat-label">Accepted</div>
          <div className="po-stat-value accepted">{acceptedCount}</div>
        </div>
        <div className="po-stat-card">
          <div className="po-stat-label">Rejected</div>
          <div className="po-stat-value rejected">{rejectedCount}</div>
        </div>
        <div className="po-stat-card">
          <div className="po-stat-label">Total Qty</div>
          <div className="po-stat-value">{totalQty}</div>
        </div>
      </div>

      <div className="po-table-wrap">
        <table className="inventory-table po-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Tax</th>
              <th>Amount</th>
              <th>Expense Name</th>
              <th>Expense Amount</th>
              <th>Total</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="12" className="empty-row">
                  Loading purchase orders...
                </td>
              </tr>
            )}

            {!loading && tableRows.length === 0 && (
              <tr>
                <td colSpan="12" className="empty-row">
                  No purchase orders found
                </td>
              </tr>
            )}

            {!loading &&
              tableRows.map((o) => (
                <tr key={o.key}>
                  <td>{o.order_id}</td>
                  <td>
                    {o.order_date
                      ? new Date(o.order_date).toLocaleDateString("en-IN")
                      : o.created_at
                        ? new Date(o.created_at).toLocaleDateString("en-IN")
                        : "-"}
                  </td>
                  <td>{o.supplier_name || "-"}</td>
                  <td>{o.product_name}</td>
                  <td>{o.quantity}</td>
                  <td>{o.tax_amount === null ? "-" : formatCurrency(o.tax_amount)}</td>
                  <td>{o.product_amount === null ? "-" : formatCurrency(o.product_amount)}</td>
                  <td>{o.expense_name}</td>
                  <td>{o.expense_amount === null ? "-" : formatCurrency(o.expense_amount)}</td>
                  <td>{formatCurrency(o.total)}</td>
                  <td>
                    {(o.status || "").toUpperCase() === "PENDING" ? (
                      <div className="status-actions">
                        <button
                          className="accept-btn"
                          onClick={() => handleAcceptOrder(o.action_id)}
                          title="Accept order"
                        >
                          <Check size={13} /> Accept
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() => handleRejectOrder(o.action_id)}
                          title="Reject order"
                        >
                          <X size={13} /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className={`po-status ${(o.status || "").toLowerCase()}`}>{o.status || "-"}</span>
                    )}
                    {o.status === "REJECTED" && o.reject_reason ? ` (${o.reject_reason})` : ""}
                  </td>
                  <td className="po-actions">
                    <button
                      className="view-btn"
                      onClick={() =>
                        navigate(`/purchase/history/${o.supplier_id}`, {
                          state: { supplier_name: o.supplier_name },
                        })
                      }
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showAcceptModal && (
        <div className="modal-overlay" onClick={() => setShowAcceptModal(false)}>
          <div className="modal-content accept-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Fill Details & Accept</h3>
              <button className="modal-close-btn" onClick={() => setShowAcceptModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="accept-items-panel">
              {acceptItems.map((item, index) => (
                <div className="accept-item-card" key={item.id || index}>
                  <div className="accept-item-title">Item {index + 1}</div>
                  <div className="accept-item-grid">
                    <div className="form-group compact">
                      <label>
                        Product Name <span className="required">*</span>
                      </label>
                      <input
                        className="form-input"
                        value={item.product_name}
                        onChange={(e) => updateAcceptItem(index, "product_name", e.target.value)}
                        placeholder="Product name"
                      />
                    </div>
                    <div className="form-group compact">
                      <label>
                        Category <span className="required">*</span>
                      </label>
                      <input
                        className="form-input"
                        value={item.category}
                        onChange={(e) => updateAcceptItem(index, "category", e.target.value)}
                        placeholder="Category"
                      />
                    </div>
                    <div className="form-group compact">
                      <label>
                        Qty <span className="required">*</span>
                      </label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => updateAcceptItem(index, "quantity", e.target.value)}
                        placeholder="Qty"
                      />
                    </div>
                    <div className="form-group compact">
                      <label>
                        Rate <span className="required">*</span>
                      </label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={item.rate}
                        onChange={(e) => updateAcceptItem(index, "rate", e.target.value)}
                        placeholder="Rate"
                      />
                    </div>
                    <div className="form-group compact">
                      <label>Tax</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={item.tax_amount}
                        onChange={(e) => updateAcceptItem(index, "tax_amount", e.target.value)}
                        placeholder="Tax"
                      />
                    </div>
                    <div className="accept-item-amount">
                      <span>Amount</span>
                      <strong>
                        {formatCurrency(
                          Number(item.quantity || 0) * Number(item.rate || 0) +
                            Number(item.tax_amount || 0)
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="form-group">
              <label htmlFor="accept-remark">
                Remark <span className="required">*</span>
              </label>
              <textarea
                id="accept-remark"
                value={acceptRemark}
                onChange={(e) => setAcceptRemark(e.target.value)}
                placeholder="Enter your remark here..."
                rows="4"
                className="form-textarea"
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAcceptModal(false)}>
                Cancel
              </button>
              <button className="btn-primary accept" onClick={handleAcceptSubmit}>
                <Check size={16} /> Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-with-icon">
                <AlertCircle size={20} className="modal-icon reject-icon" />
                <h3 className="modal-title">Reject Purchase Order</h3>
              </div>
              <button className="modal-close-btn" onClick={() => setShowRejectModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="form-group">
              <label htmlFor="reject-reason">
                Reason <span className="required">*</span>
              </label>
              <textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows="4"
                className="form-textarea"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="expense-name">
                Expense Name <span className="optional">(Optional)</span>
              </label>
              <input
                id="expense-name"
                type="text"
                value={expenseName}
                onChange={(e) => setExpenseName(e.target.value)}
                placeholder="Enter expense name..."
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="expense-amount">
                Expense Amount <span className="optional">(Optional)</span>
              </label>
              <input
                id="expense-amount"
                type="number"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="Enter expense amount..."
                min="0"
                step="0.01"
                className="form-input"
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button className="btn-primary reject" onClick={handleRejectSubmit}>
                <X size={16} /> Reject Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
